import React, { useState } from 'react';

import { useParams } from 'react-router';
import { PageLayout } from '~/shared/PageLayout';
import {
	getLegacyReferralOptions,
	LegacyReferralMessageTypeEnum,
	LegacyReferralNotificationMessageStatusEnum,
	LegacyReferralNotificationResponseStatusEnum,
	LegacyReferralStatusEnum,
	makeLegacyReferralDecisionOptions,
	updateLegacyReferralOptions
} from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Section } from '~/shared/section/Section';
import { StatusPill } from '~/shared/StatusPill';
import { Calendar, CheckIcon, MapPin, Minus, Plus, Stethoscope, XIcon } from 'lucide-react';
import { LegacyReferralNotificationsTable } from './notifications/LegacyReferralNotificationsTable';
import { LegacyReferralMessagesTable } from './notifications/LegacyReferralMessagesTable';
import { LegacyReferralProvider } from './LegacyReferralContext';
import { ReferralAssignments } from './assignments/ReferralAssignments';
import { Button } from '~/shared/components/buttons/Button';
import { Modal } from '~/shared/components/modal/Modal';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { formatToDateTwoYearAndTimeOrNull } from '~/util/date';
import { TextAreaField } from '~/shared/forms/controls/base-fields/TextAreaField';
import { AddDisciplineFormModal, CancelDisciplineFormModal } from './CancelDisciplineFormModal';
import { SectionTitle } from '~/shared/section/SectionTitle';
import { formatAddress, getDisciplineAbbreviations, getReferralStatusIntent, getReferralStatusLabel } from './referralHelpers';

export function LegacyReferralPage() {
	const { referralId } = useParams<'referralId'>();
	const { data } = useSuspenseQuery(getLegacyReferralOptions(referralId!));
	const makeDecision = useMutation({
		...makeLegacyReferralDecisionOptions,
		invalidateQueryKeys: [getLegacyReferralOptions(referralId!).queryKey, getSearchQueryKey('legacy-referral')]
	});
	const updateReferral = useMutation({
		...updateLegacyReferralOptions,
		invalidateQueryKeys: [getLegacyReferralOptions(referralId!).queryKey, getSearchQueryKey('legacy-referral')]
	});
	const [modalStatus, setModalStatus] = useState<{ type: 'status' | 'cancel-discipline' | 'add-discipline'; show: boolean }>({
		type: 'status',
		show: false
	});
	const [referralNotes, setReferralNotes] = useState(data.referralNotes || '');

	const responded = data.notifications.filter(x => x.responseStatusId === LegacyReferralNotificationResponseStatusEnum.Responded);
	const distances = data.notifications.filter(x => typeof x.distance === 'number').map(x => x.distance!);
	const averageDistance = distances.length > 0 ? (distances.reduce((x, y) => x + y, 0) / distances.length).toFixed(1) : '-';
	const teamsSent = data.messages.filter(
		x => x.messageTypeId === LegacyReferralMessageTypeEnum.NewReferral && x.statusId === LegacyReferralNotificationMessageStatusEnum.Sent
	).length;
	const teamsFailed = data.messages.filter(x => x.statusId === LegacyReferralNotificationMessageStatusEnum.Error).length;
	const needStaffing = getDisciplineAbbreviations(data.disciplinesNeedStaffing);

	const handleDecision = (accepted: boolean) => {
		makeDecision.mutate(
			{
				referralId,
				accepted
			},
			{
				onSettled: () => {
					setModalStatus({ type: 'status', show: false });
				}
			}
		);
	};

	const handleUpdateNotes = () => {
		updateReferral.mutate({
			referralId,
			referralNotes
		});
	};

	return (
		<PageLayout pageHeader={{ title: 'Referral Details' }}>
			<div className="space-y-6">
				<Section
					header={
						<SectionTitle
							title={`${data.patientLastName}, ${data.patientFirstName} (#${data.mrNumber})`}
							status={
								<div className="mt-1 flex items-center gap-4">
									<StatusPill intent={getReferralStatusIntent(data.statusId)}>{getReferralStatusLabel(data.statusId)}</StatusPill>
									{data.priority && <StatusPill intent="warning">Priority</StatusPill>}
									{data.statusId !== LegacyReferralStatusEnum.Ntfc && (
										<Button
											actionType="add"
											appearance="outline"
											size="xs"
											onClick={() => setModalStatus({ type: 'status', show: true })}
											label="Accept/Reject"
										/>
									)}
									{modalStatus.type === 'status' && modalStatus.show && (
										<Modal title="Accept/Reject" closeModal={() => setModalStatus({ type: 'status', show: false })}>
											<p className="text-gray-700">Do you want to accept this referral?</p>
											<p className="mt-2 text-sm text-gray-500">
												Note: Accepting will allow you to staff this referral. Rejecting will remove this referral from your
												queue and prevent you from staffing it.
											</p>
											<div className="mt-6 flex justify-end gap-2">
												<Button
													intent="destructive"
													autoFocus
													onClick={() => handleDecision(false)}
													label="Reject"
													Icon={XIcon}
													isPending={makeDecision.isPending}
												/>
												<Button
													autoFocus
													onClick={() => handleDecision(true)}
													label="Accept"
													Icon={CheckIcon}
													isPending={makeDecision.isPending}
												/>
											</div>
										</Modal>
									)}
								</div>
							}
						/>
					}
					className="p-12"
				>
					<div className="mb-2 flex items-start justify-between gap-6 border-b border-gray-100 py-2">
						<div className="flex min-w-0 flex-1 flex-col gap-4">
							<div className="flex items-center gap-4 text-sm text-gray-500">
								<span className="flex items-center gap-1">
									<MapPin size={12} />
									{formatAddress(data.address)}
								</span>
								<span className="flex items-center gap-1">
									<Calendar size={12} />
									SOC {new Date(data.expectedStartOfCareDate).toLocaleDateString()}
								</span>
							</div>
						</div>
						<div className="whitespace-nowrap pt-1 text-xs text-gray-400">
							Created {formatToDateTwoYearAndTimeOrNull(data.dateCreated)}
						</div>
					</div>
					<div className="mb-6 flex flex-wrap items-center gap-8">
						<div className="flex items-center gap-1.5 text-sm text-gray-700">
							<Stethoscope size={14} className="text-gray-400" />
							<span className="mr-1 text-xs font-medium uppercase tracking-wide text-gray-500">Diagnosis</span>
							<span className="font-semibold text-gray-900">{data.primaryDiagnoses}</span>
						</div>
						<div className="h-4 w-px bg-gray-200" />
						<Stat label="Avg Dist" value={`${averageDistance} mi`} />
						<div className="h-4 w-px bg-gray-200" />
						<Stat label="Notified" value={data.notifications.length} />
						<Stat label="Teams Sent" value={teamsSent} />
						<Stat label="Teams Failed" value={<span className={teamsFailed > 0 ? 'text-red-700' : undefined}>{teamsFailed}</span>} />
						<Stat label="Resp" value={responded.length} />
						<Stat label="Yes" value={responded.filter(x => x.accepted === true).length} />
						<div className="h-4 w-px bg-gray-200" />
						<Stat
							label="Staffed"
							value={<span className={data.fullyStaffed ? 'text-green-700' : 'text-gray-400'}>{data.fullyStaffed ? 'Yes' : 'No'}</span>}
						/>
						<div className="h-4 w-px bg-gray-200" />
						<div className="flex flex-col items-start gap-0.5">
							<span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Needed</span>
							<div className="flex gap-1">
								{getDisciplineAbbreviations(data.disciplinesNeeded).map(x => (
									<span key={x} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-700">
										{x}
									</span>
								))}
							</div>
						</div>
						{needStaffing.length > 0 && (
							<div className="flex flex-col items-start gap-0.5">
								<span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Remaining</span>
								<div className="flex gap-1">
									{needStaffing.map(x => (
										<span
											key={x}
											className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700"
										>
											{x}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
					<div className="flex flex-col items-end gap-2">
						<TextAreaField
							label="Referral Notes"
							hideLabel
							value={referralNotes}
							className="w-full"
							onChange={e => setReferralNotes(e)}
							inputProps={{
								placeholder: 'Add notes'
							}}
						/>
						<Button
							appearance="outline"
							onClick={handleUpdateNotes}
							label="Update Notes"
							Icon={CheckIcon}
							isPending={updateReferral.isPending}
						/>
					</div>
				</Section>
				<Section
					header={
						<SectionTitle
							title="Assignments"
							actions={
								<>
									<Button
										appearance="outline"
										size="xs"
										onClick={() => setModalStatus({ type: 'add-discipline', show: true })}
										Icon={Plus}
										label="Add Discipline"
									/>
									<Button
										appearance="outline"
										intent="destructive"
										size="xs"
										onClick={() => setModalStatus({ type: 'cancel-discipline', show: true })}
										Icon={Minus}
										label="Remove Discipline"
									/>
								</>
							}
						/>
					}
					className="p-12"
				>
					<ReferralAssignments referral={data} />
					{modalStatus.type === 'cancel-discipline' && modalStatus.show && (
						<CancelDisciplineFormModal
							legacyReferral={data}
							closeModal={() => setModalStatus({ type: 'cancel-discipline', show: false })}
						/>
					)}
					{modalStatus.type === 'add-discipline' && modalStatus.show && (
						<AddDisciplineFormModal legacyReferral={data} closeModal={() => setModalStatus({ type: 'add-discipline', show: false })} />
					)}
				</Section>
				<Section header={<SectionTitle title="Notifications" />} className="p-12">
					<LegacyReferralProvider referral={data}>
						<LegacyReferralNotificationsTable rows={data.notifications} />
					</LegacyReferralProvider>
				</Section>
				<Section header={<SectionTitle title="Teams Messages" />} className="p-12">
					<LegacyReferralMessagesTable rows={data.messages} />
				</Section>
			</div>
		</PageLayout>
	);
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<div className="flex flex-col items-center gap-0.5">
			<span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
			<span className="text-sm font-semibold text-gray-800">{value}</span>
		</div>
	);
}
